<template>
  <div
    v-show="!isTopWrapperInit || (isTopWrapperInit && !isShowBanner)"
    id="topWrapper"
    class="workbench-my-app__wrapper"
  >
    <workbench-info></workbench-info>
    <my-app></my-app>
  </div>
  <banner-top v-if="isUserinfoReady && isTopWrapperInit && isShowBanner"></banner-top>

  <!-- 入口迁移公告 -->
  <migrate-tips v-if="isNewTabEntry"></migrate-tips>

  <div :class="['main-body', { 'no-margintop': isNewTabEntry }]">
    <div class="main-body__bg"></div>
    <template-type></template-type>
    <app-list></app-list>
  </div>
  <help-sidebar></help-sidebar>

  <!-- 关闭提示弹窗 -->
  <template-ask-dialog v-if="isEnableBeforeCloseTip && isShowCloseTabDialog"></template-ask-dialog>

  <!-- 入口迁移弹窗 -->
  <migrate-dialog v-if="migrateDialogVisible" @got-it="migrateDialogVisible = false"></migrate-dialog>
</template>

<script setup lang="ts">
import AppList from '@/features/app-list/app-list.vue';
import BannerTop from '@/features/banner-top/banner-top.vue';
import HelpSidebar from '@/features/help-icon/help-sidebar.vue';
import MyApp from '@/features/my-app/my-app.vue';
import TemplateType from '@/features/template-type/template-type.vue';
import WorkbenchInfo from '@/features/workbench-info/workbench-info.vue';
import { usePTeamStore, usePUserStore } from '@/store';
import { dwCollect } from '@/utils/dw-collect';
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { bindBeforeCloseTabEvent, isCloseTabTipsNeverAsk, isCloseTabTipsShowed } from '../close-tab/close-tab.tools';
import MigrateTips from './migrate-tips.vue';
import MigrateDialog from './migrate-dialog.vue';
import { getClientSrc } from '@/utils/client';
import { isWpsEnv } from '@/utils/get-user-info';
import { setLocalStoragePrivate, getLocalStoragePrivate } from '@/utils/storage-tools';
const ENTRY_MIGRATE_KEY = 'ENTRY_MIGRATE_V2';

const userStore = usePUserStore();
/** 用户是否登录 */
const isLogin = computed(() => {
  return userStore.isLogin;
});
const isUserinfoReady = computed(() => {
  return userStore.isReady;
});

const teamStore = usePTeamStore();
/** 是否显示工作区 */
const isShowWorkbench = computed(() => {
  return teamStore.isShowWorkbench;
});
/** 是否展示 banner */
const isShowBanner = computed(() => {
  return !isLogin.value || !isShowWorkbench.value;
});

// 判断是否要展示入口迁移相关的公告
const isNewTabEntry = ref(false);
// const isNewTabEntry = ref('wps_app');
const migrateDialogVisible = ref(false);
if (isWpsEnv()) {
  getClientSrc().then((src) => {
    isNewTabEntry.value = src === 'wps_app';
  });
}

/** 是否为工作区拥有者或管理员 */
const isWorkbenchOwnerOrManager = computed(() => {
  return teamStore.isWorkbenchOwner || teamStore.isWorkbenchManager;
});

/**
 * 用户从新建页进入到新建应用页时，针对工作区拥有者或管理员，主动推送信息收集弹窗
 */
const postUserCollectFromNewTab = () => {
  console.log('----用户从新建页进入', isWorkbenchOwnerOrManager.value);
  // 判断用户是否是工作区拥有者或管理员，是的话
  if (isWorkbenchOwnerOrManager.value) {
    postUserCollect();
  }
};

/**
 * 用户从非新建页（如应用市场、链接等）进入到新建应用页时，针对满足以下任一条件的用户主动推送信息收集弹窗：
      • 工作区拥有者或管理员
      • 已登录WPS账号但未创建或加入过工作区的用户
 */
const postUserCollectUnFromNewTab = () => {
  console.log('----用户从非新建页进入', isWorkbenchOwnerOrManager.value || !teamStore.statics?.curr_team_info);
  // 判断非新建页，且（用户是否是工作区拥有者或管理员或者是否未创建或加入过工作区，满足任一触发收集弹窗）
  if (!isNewTabEntry.value && (isWorkbenchOwnerOrManager.value || !teamStore.statics?.curr_team_info)) {
    postUserCollect();
  }
};

// 判断是否正式环境
const isProduction = computed(() => import.meta.env.MODE === 'production');

/**
 * 推送信息收集弹窗，分从新建页和非新建页进入新建应用页场景
 * 1. 用户从新建页进入到新建应用页时，针对工作区拥有者或管理员，主动推送信息收集弹窗
 * 2. 用户从非新建页（如应用市场、链接等）进入到新建应用页时，针对满足以下任一条件的用户主动推送信息收集弹窗：
      • 工作区拥有者或管理员
      • 已登录WPS账号但未创建或加入过工作区的用户
 */
const postUserCollect = () => {
  const teamId = teamStore.statics?.curr_team_info?.team_id ?? '';
  // 触发用户信息收集弹窗，sdk内部会判断是否需要展示
  !!window.invokeToTeamUserCollect &&
    window.invokeToTeamUserCollect({
      params: {
        app_id: isProduction.value ? 'AK20220310TWLGYT' : 'AK20210518HIRGVN', // 应用的在 wps 侧的 app_id
        is_owner: true, // 业务对目前登录的用户在团队中的身份判断，收集管理员/拥有者的信息
        app_name: '新建应用', // 应用名称
        partner_id: teamId, // 指业务层面定义的团队的id，需要与之前埋点上报中的_group_id ,团队业务表同步过来的团队id一致
        config_key: isProduction.value ? '3f67be12d4e24fe2a6cae521eaf34e64' : '91e0389d735b4015be8a62d69f8722a6', // 配置后由我们的研发同学提供
        open_id: '', // 用户id，非wps自研项目必传。open_id 是每个用户进入应用时，WPS开发平台会生成的一个区分用户的唯一性id，现在应用的页面埋点上报时都会用到该id
      },
      callBack: () => {
        // 信息收集结束后的回调事件，通常其他业务流程可在此处继续进行
      },
    });
};

// 判断是否要展示入口迁移弹窗提醒  1.客户端内 2.新建页场景 3.弹窗仅根据设备展示一次 4.已存在工作区
watch(
  () => teamStore.statics,
  () => {
    console.log('-----isProduction', isProduction.value, isNewTabEntry.value, teamStore.statics);
    // 新建页入口场景
    if (isNewTabEntry.value && teamStore.statics?.curr_team_info) {
      const hasShow = !!getLocalStoragePrivate({
        id: ENTRY_MIGRATE_KEY,
        key: ENTRY_MIGRATE_KEY,
      })?.showed;
      if (!hasShow) {
        migrateDialogVisible.value = true;
        setLocalStoragePrivate({
          id: ENTRY_MIGRATE_KEY,
          key: ENTRY_MIGRATE_KEY,
          data: {
            showed: true,
          },
        });
      } else {
        postUserCollectFromNewTab();
      }
    }
    // 非新建页入口场景
    if (!isNewTabEntry.value && !!teamStore.statics) {
      postUserCollectUnFromNewTab();
    }
  },
  { immediate: true }
);

/** 顶部块是否初始化完成  */
const isTopWrapperInit = computed(() => {
  return teamStore.isInitStaticsData;
});

// 处理埋点
const isInitStaticsData = computed(() => {
  return teamStore.isInitStaticsData;
});
watch(
  () => isInitStaticsData.value,
  (val) => {
    if (val) {
      dwCollect({ page: 'add_app', action: 'show' });
    }
  },
  { immediate: true }
);

// #region 处理关闭浏览器tab前提示
const templateAskDialog = defineAsyncComponent(() => import('@/features/close-tab/template-ask-dialog.vue'));

const isEnableBeforeCloseTip = computed(() => {
  // 条件1：用户所在工作区均无应用。
  const isNoWorkspaceApp = isInitStaticsData.value && (!isShowWorkbench.value || !teamStore.statics?.team_apps?.length);
  if (!isNoWorkspaceApp) {
    return false;
  }
  // 条件2：用户当前设备当天首次关闭。（每个设备ID每天只弹出一次）
  const isShowed = isCloseTabTipsShowed();
  if (isShowed) {
    return false;
  }
  // 条件3：用户未勾选“不再提示”。
  const isNeverAsk = isCloseTabTipsNeverAsk();
  if (isNeverAsk) {
    return false;
  }

  return true;
});

/** 显示关闭前提示弹窗 */
const isShowCloseTabDialog = ref(false);
bindBeforeCloseTabEvent(() => {
  isShowCloseTabDialog.value = true;
});
// #endregion
</script>

<style lang="scss" scoped>
.workbench-my-app {
  &__wrapper {
    display: flex;
    flex-direction: column;
    flex: none;
    font-size: 0;
    box-sizing: border-box;
    min-width: var(--lowcode-min-width);
    position: relative;
    margin: 24px 24px 0 24px;
    min-height: 284px;
    border-radius: 8px;
  }
}

.main-body {
  position: relative;
  min-width: var(--lowcode-min-width);
  border-radius: 8px;
  margin: 24px;
  flex-grow: 1;
  flex-shrink: 0;

  &__bg {
    position: absolute;
    z-index: 0;
    left: 0;
    right: 0;
    height: 290px;
    background: var(--lowcode-custom-bg-105);
    border-radius: 8px;
  }

  &.no-margintop {
    margin-top: 0 !important;
  }
}
</style>
